import { useEffect, useState, useCallback } from "react";
import { api, imgSrc } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Upload, Trash2, Star, GripVertical, Film, Play } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "./shared";

const CATEGORIES = ["cabins", "art", "grounds", "eatery", "experiences"];

export default function GalleryTab() {
    const { t } = useT();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [category, setCategory] = useState("cabins");
    const [caption, setCaption] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);

    const refresh = useCallback(async () => {
        const r = await api.get("/gallery", { params: filter === "all" ? {} : { category: filter } });
        setItems(r.data);
    }, [filter]);

    useEffect(() => { refresh(); }, [refresh]);

    const toggleFeatured = async (img) => {
        const next = !img.featured;
        setItems((xs) => xs.map((x) => x.id === img.id ? { ...x, featured: next } : x));
        try {
            await api.patch(`/admin/gallery/${img.id}`, { featured: next });
        } catch (err) {
            setItems((xs) => xs.map((x) => x.id === img.id ? { ...x, featured: !next } : x));
            toast.error(err.response?.data?.detail || "Update failed");
        }
    };

    const onDrop = async (toIndex) => {
        if (dragIndex === null || dragIndex === toIndex) { setDragIndex(null); return; }
        const reordered = [...items];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(toIndex, 0, moved);
        setItems(reordered);
        setDragIndex(null);
        try {
            await api.post("/admin/gallery/reorder", { ordered_ids: reordered.map((x) => x.id) });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reorder failed");
            refresh();
        }
    };

    const onPickFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (files.length === 0) return;
        setUploading(true);
        try {
            for (const file of files) {
                if (!file.type.startsWith("image/")) { toast.error(`${file.name}: not an image`); continue; }
                const fd = new FormData();
                fd.append("file", file);
                fd.append("category", category);
                fd.append("caption", caption);
                try {
                    await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
                } catch (err) {
                    toast.error(err.response?.data?.detail || `${file.name}: upload failed`);
                }
            }
            setCaption("");
            toast.success(t("admin.galleryUploaded"));
            refresh();
        } finally {
            setUploading(false);
        }
    };

    const onPickVideos = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (files.length === 0) return;
        setUploadingVideo(true);
        try {
            for (const file of files) {
                if (!file.type.startsWith("video/")) { toast.error(`${file.name}: not a video`); continue; }
                const fd = new FormData();
                fd.append("file", file);
                fd.append("category", category);
                fd.append("caption", caption);
                try {
                    await api.post("/admin/gallery/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
                } catch (err) {
                    toast.error(err.response?.data?.detail || `${file.name}: upload failed`);
                }
            }
            setCaption("");
            toast.success(t("admin.galleryVideoUploaded"));
            refresh();
        } finally {
            setUploadingVideo(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/admin/gallery/${id}`);
            setItems((xs) => xs.filter((x) => x.id !== id));
            toast.success(t("admin.galleryRemoved"));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    const changeCategory = async (id, cat) => {
        try {
            await api.patch(`/admin/gallery/${id}`, { category: cat });
            setItems((xs) => xs.map((x) => x.id === id ? { ...x, category: cat } : x));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        }
    };

    return (
        <div data-testid="admin-gallery-tab">
            <PageHeader eyebrow={t("admin.gallery")} title={t("admin.galleryTitle")} subtitle={t("admin.gallerySubtitle")} />

            {/* Uploader */}
            <div className="border border-espresso/15 bg-bone2/30 p-6 mb-10" data-testid="gallery-uploader">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                    <div>
                        <label className="eyebrow block mb-2">{t("admin.galleryCategory")}</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="gallery-upload-category" className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent focus:border-forest outline-none">
                            {CATEGORIES.map((c) => <option key={c} value={c}>{t(`gallery.cat.${c}`)}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="eyebrow block mb-2">{t("admin.galleryCaption")}</label>
                        <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={t("admin.galleryCaptionPlaceholder")} data-testid="gallery-upload-caption" className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent focus:border-forest outline-none" />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <label className="border border-dashed border-espresso/30 bg-bone2/40 px-4 py-3 flex items-center justify-center gap-2 cursor-pointer hover:bg-bone2 transition-colors text-xs uppercase tracking-widest2 text-espresso" data-testid="gallery-upload-dropzone">
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/avif" multiple onChange={onPickFiles} className="hidden" data-testid="gallery-file-input" disabled={uploading || uploadingVideo} />
                        <Upload size={16} className="text-forest" />
                        {uploading ? t("admin.galleryUploading") : t("admin.galleryUpload")}
                    </label>
                    <label className="border border-dashed border-espresso/30 bg-bone2/40 px-4 py-3 flex items-center justify-center gap-2 cursor-pointer hover:bg-bone2 transition-colors text-xs uppercase tracking-widest2 text-espresso" data-testid="gallery-upload-video-dropzone">
                        <input type="file" accept="video/mp4,video/webm" multiple onChange={onPickVideos} className="hidden" data-testid="gallery-video-input" disabled={uploading || uploadingVideo} />
                        <Film size={16} className="text-terracotta" />
                        {uploadingVideo ? t("admin.galleryUploadingVideo") : t("admin.galleryUploadVideo")}
                    </label>
                </div>
                <p className="text-[11px] text-muted2 mt-3">{t("admin.galleryHint")} {t("admin.galleryVideoHint")}</p>
            </div>

            {/* Filter */}
            <div className="flex flex-wrap gap-2 mb-6" data-testid="gallery-filter">
                {["all", ...CATEGORIES].map((c) => (
                    <button
                        key={c}
                        onClick={() => setFilter(c)}
                        data-testid={`gallery-filter-${c}`}
                        className={`text-xs uppercase tracking-widest2 px-4 py-2 border transition-colors ${filter === c ? "bg-forest text-bone border-forest" : "border-espresso/20 text-espresso hover:border-forest"}`}
                    >
                        {c === "all" ? t("gallery.cat.all") : t(`gallery.cat.${c}`)}
                    </button>
                ))}
            </div>

            {/* Grid */}
            {items.length === 0 ? (
                <p className="text-sm text-muted2 italic" data-testid="gallery-empty">{t("admin.galleryEmpty")}</p>
            ) : (
                <>
                    {filter === "all" && <p className="text-[11px] text-muted2 mb-3" data-testid="gallery-reorder-hint">{t("admin.galleryReorderHint")}</p>}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
                    {items.map((img, idx) => (
                        <div
                            key={img.id}
                            className={`relative group border bg-bone2 overflow-hidden transition-colors ${dragIndex === idx ? "border-forest opacity-60" : "border-espresso/15"}`}
                            data-testid={`gallery-item-${img.id}`}
                            draggable={filter === "all"}
                            onDragStart={() => filter === "all" && setDragIndex(idx)}
                            onDragOver={(e) => { if (filter === "all") e.preventDefault(); }}
                            onDrop={() => filter === "all" && onDrop(idx)}
                        >
                            <div className="aspect-[4/3] overflow-hidden relative">
                                {img.media_type === "video" ? (
                                    <>
                                        <video src={imgSrc(img.url)} preload="metadata" muted className="w-full h-full object-cover" />
                                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                            <span className="bg-espresso/60 text-bone rounded-full p-2"><Play size={18} fill="currentColor" /></span>
                                        </span>
                                        <span className="absolute bottom-1.5 left-1.5 bg-terracotta text-bone text-[9px] uppercase tracking-widest2 px-1.5 py-0.5 rounded-sm">Video</span>
                                    </>
                                ) : (
                                    <img src={imgSrc(img.url)} alt={img.caption || "gallery"} className="w-full h-full object-cover" />
                                )}
                                {filter === "all" && (
                                    <span className="absolute top-1.5 left-1.5 bg-espresso/70 text-bone p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" title="Drag to reorder">
                                        <GripVertical size={13} />
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => toggleFeatured(img)}
                                    data-testid={`gallery-feature-${img.id}`}
                                    title={img.featured ? t("admin.galleryUnfeature") : t("admin.galleryFeature")}
                                    className={`absolute top-1.5 right-1.5 p-1.5 rounded-sm transition-colors ${img.featured ? "bg-gold text-espresso" : "bg-espresso/60 text-bone opacity-0 group-hover:opacity-100"}`}
                                >
                                    <Star size={14} fill={img.featured ? "currentColor" : "none"} />
                                </button>
                            </div>
                            <div className="p-3">
                                {img.caption && <div className="text-sm font-serif mb-2 truncate">{img.caption}</div>}
                                <div className="flex items-center justify-between gap-2">
                                    <select
                                        value={img.category}
                                        onChange={(e) => changeCategory(img.id, e.target.value)}
                                        data-testid={`gallery-item-category-${img.id}`}
                                        className="text-[11px] uppercase tracking-widest2 border border-espresso/20 px-2 py-1 bg-transparent"
                                    >
                                        {CATEGORIES.map((c) => <option key={c} value={c}>{t(`gallery.cat.${c}`)}</option>)}
                                    </select>
                                    <button type="button" onClick={() => remove(img.id)} data-testid={`gallery-delete-${img.id}`} className="text-terracotta hover:text-forest">
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    </div>
                </>
            )}
        </div>
    );
}
